import type { Plugin, SegmentEvent } from '@segment/analytics-next'
import { segmentTrackEventRequiredFields } from '../generated/segmentEventRequiredFields'
import {
  getMissingEventProperties,
  getTrackEventAllowedFields,
  removeNullExtraEventProperties,
} from './missingEventProperties'

export { removeNullExtraEventProperties } from './missingEventProperties'

export const applyDefaultNullEventProperties = (
  properties: { [key: string]: unknown },
  requiredProperties: readonly string[]
) => {
  const missingProperties = getMissingEventProperties(
    requiredProperties,
    properties
  )

  if (missingProperties.length === 0) {
    return properties
  }

  return {
    ...properties,
    ...Object.fromEntries(missingProperties.map(property => [property, null])),
  }
}

export const applyDefaultNullToTrackEvent = (
  event: SegmentEvent
): SegmentEvent => {
  if (event.type !== 'track' || !event.event) {
    return event
  }

  const requiredProperties =
    segmentTrackEventRequiredFields[
      event.event as keyof typeof segmentTrackEventRequiredFields
    ]

  if (!requiredProperties?.length) {
    return event
  }

  const propertiesWithoutNullExtras = removeNullExtraEventProperties(
    event.properties ?? {},
    getTrackEventAllowedFields(event.event)
  )

  event.properties = applyDefaultNullEventProperties(
    propertiesWithoutNullExtras,
    requiredProperties
  )

  return event
}

export const defaultNullEventPropertiesPlugin: Plugin = {
  name: 'Default Null Event Properties Interceptor',
  type: 'before',
  version: '1.0.0',
  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: ctx => {
    ctx.event = applyDefaultNullToTrackEvent(ctx.event)
    return ctx
  },
}
